import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useIsNarrow, gridCols, LoadError } from './ui';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';

// EMOJICLEAN: status is conveyed by the per-status background + text colour below and the label;
// the pictographic icon field was removed.
const STATUS_META = {
  submitted:       { bg: '#f3f4f6', color: '#374151' },
  under_review:    { bg: '#dbeafe', color: '#1d4ed8' },
  approved:        { bg: '#d1fae5', color: '#065f46' },
  rejected:        { bg: '#fee2e2', color: '#991b1b' },
  in_development:  { bg: '#ede9fe', color: '#5b21b6' },
  in_testing:      { bg: '#fef3c7', color: '#92400e' },
  live:            { bg: '#bbf7d0', color: '#166534' },
  ko:              { bg: '#fecaca', color: '#7f1d1d' },
};

const NOTIFY_STATUSES = new Set(['approved', 'in_testing', 'live', 'rejected']);

function fmt(d) {
  if (!d) return '-';
  return toDisplayDate(d).toLocaleDateString('en-GB');
}

function StatusBadge({ status, label }) {
  const m = STATUS_META[status] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{ padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600,
      background: m.bg, color: m.color, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {label || status}
    </span>
  );
}

function ApplicationRequestStatus({ currentUser, onBack, onSubmitNew }) {
  const { t } = useTranslation();
  // RESPONSIVE Phase 2: the two-column info grid reflows to one column below ~900px.
  const isNarrow = useIsNarrow();
  const gridStyle = { ...s.grid, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') };
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  // MYREQFAILSAFE: distinct from `loading`. A failed load left `requests` at its empty-array
  // default, and the empty state also shows a "Submit your first request" button - a failure
  // must not invite a duplicate submission, so this flag routes failures to a separate branch
  // that omits that button entirely.
  const [loadFailed, setLoadFailed] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadFailed(false);
    axios.get(`${API}/application-requests/mine`, { params: { submitted_by: currentUser } })
      .then(res => setRequests(res.data))
      .catch(() => { setRequests([]); setLoadFailed(true); })
      .finally(() => setLoading(false));
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const notifiable = requests.filter(r => NOTIFY_STATUSES.has(r.status));

  return (
    <div style={s.container}>
      {onBack && (
        <button
          style={s.backBtn}
          onClick={onBack}
          onMouseEnter={e => { e.currentTarget.style.color = '#c9a84c'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#1a1a2e'; }}
        >
          ← Back to dashboard
        </button>
      )}
      <div style={s.headerRow}>
        <div>
          <h1 style={s.title}>{t('appStatus.title')}</h1>
          <p style={s.subtitle}>{t(`appStatus.subtitle`, { count: requests.length })}</p>
        </div>
        <button style={s.newBtn} onClick={onSubmitNew}>{t('appStatus.newRequest')}</button>
      </div>

      {/* Status update notifications */}
      {notifiable.length > 0 && (
        <div style={s.notifBox}>
          <p style={s.notifTitle}>{t('appStatus.statusUpdates')}</p>
          {notifiable.map(r => {
            const m = STATUS_META[r.status] || {};
            return (
              <div key={r.id} style={s.notifItem} onClick={() => setSelected(r)}>
                <span style={s.notifIcon}>{m.icon}</span>
                <span style={s.notifText}>
                  <strong>{r.title}</strong>: {r.status}
                  {r.rejection_reason && <span style={{ color: '#991b1b' }}> · {r.rejection_reason}</span>}
                  {r.ko_reason && <span style={{ color: '#7f1d1d' }}> · {r.ko_reason}</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <div style={s.detailCard}>
          <div style={s.detailHeader}>
            <div>
              <StatusBadge status={selected.status} label={selected.status} />
              <h2 style={s.detailTitle}>{selected.title}</h2>
            </div>
            <button style={s.closeBtn} onClick={() => setSelected(null)}>✕</button>
          </div>

          <div style={gridStyle}>
            <div style={s.infoBlock}><span style={s.infoLabel}>{t('appStatus.department')}</span><span style={s.infoVal}>{selected.department}</span></div>
            <div style={s.infoBlock}><span style={s.infoLabel}>{t('appStatus.submitted')}</span><span style={s.infoVal}>{fmt(selected.created_at)}</span></div>
            {selected.assigned_to && <div style={s.infoBlock}><span style={s.infoLabel}>{t('appStatus.assignedTo')}</span><span style={s.infoVal}>{selected.assigned_to_team ? t('appStatus.itTeam') : selected.assigned_to}</span></div>}
          </div>

          <div style={s.detailSection}>
            <span style={s.detailLabel}>{t('appStatus.description')}</span>
            <p style={s.detailText}>{selected.description}</p>
          </div>
          <div style={s.detailSection}>
            <span style={s.detailLabel}>{t('appStatus.problemToSolve')}</span>
            <p style={s.detailText}>{selected.problem_to_solve}</p>
          </div>
          {selected.expected_benefit && (
            <div style={s.detailSection}>
              <span style={s.detailLabel}>{t('appStatus.expectedBenefit')}</span>
              <p style={s.detailText}>{selected.expected_benefit}</p>
            </div>
          )}
          {selected.rejection_reason && (
            <div style={{ ...s.detailSection, backgroundColor: '#fff5f5', borderRadius: 8, padding: '12px 16px' }}>
              <span style={{ ...s.detailLabel, color: '#991b1b' }}>{t('appStatus.rejectionReason')}</span>
              <p style={s.detailText}>{selected.rejection_reason}</p>
            </div>
          )}
          {selected.ko_reason && (
            <div style={{ ...s.detailSection, backgroundColor: '#fff5f5', borderRadius: 8, padding: '12px 16px' }}>
              <span style={{ ...s.detailLabel, color: '#7f1d1d' }}>{t('appStatus.koReason')}</span>
              <p style={s.detailText}>{selected.ko_reason}</p>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <p style={s.empty}>{t('appStatus.loading')}</p>
      ) : loadFailed ? (
        // MYREQFAILSAFE: checked before the empty-state branch, and deliberately does not
        // include the "Submit your first request" button that branch shows below.
        <LoadError message="Could not load your requests." onRetry={load} />
      ) : requests.length === 0 ? (
        <div style={s.emptyState}>
          <p style={s.emptyTitle}>{t('appStatus.noRequests')}</p>
          <p style={s.emptyText}>{t('appStatus.noRequestsText')}</p>
          <button style={s.newBtn} onClick={onSubmitNew}>{t('appStatus.submitFirst')}</button>
        </div>
      ) : (
        <div style={s.list}>
          {requests.map(req => (
            <div key={req.id} style={s.card} onClick={() => setSelected(req === selected ? null : req)}>
              <div style={s.cardLeft}>
                <StatusBadge status={req.status} label={req.status} />
                <div style={{ marginTop: 8 }}>
                  <p style={s.cardTitle}>{req.title}</p>
                  <p style={s.cardMeta}>
                    {req.department}
                  </p>
                  <p style={s.cardDate}>{t('appStatus.submitted_prefix')} {fmt(req.created_at)}</p>
                </div>
              </div>
              {req.assigned_to || req.assigned_to_team ? (
                <span style={s.assignedBadge}>
                  {req.assigned_to_team ? t('appStatus.itTeam') : req.assigned_to}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  container: { fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: '800px', margin: '0 auto', padding: '24px', backgroundColor: '#f8f9fa' },
  backBtn: { background: 'none', border: 'none', color: '#1a1a2e', fontSize: '14px', cursor: 'pointer', padding: '0 0 16px 0', display: 'block' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px 0' },
  subtitle: { fontSize: '14px', color: '#888', margin: 0 },
  newBtn: { backgroundColor: '#1a1a2e', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  notifBox: { backgroundColor: '#fff', border: '1px solid #e0e7ff', borderRadius: 10, padding: '14px 18px', marginBottom: 20 },
  notifTitle: { fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 10px 0' },
  notifItem: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' },
  notifIcon: { fontSize: 16, flexShrink: 0, marginTop: 1 },
  notifText: { fontSize: 13, color: '#374151', lineHeight: 1.5 },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { backgroundColor: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', transition: 'box-shadow 0.15s' },
  cardLeft: { flex: 1 },
  cardTitle: { fontSize: 15, fontWeight: 600, color: '#1a1a2e', margin: '0 0 3px 0' },
  cardMeta: { fontSize: 13, color: '#6b7280', margin: '0 0 2px 0' },
  cardDate: { fontSize: 12, color: '#9ca3af', margin: 0 },
  assignedBadge: { fontSize: 12, color: '#5b21b6', backgroundColor: '#ede9fe', padding: '3px 10px', borderRadius: 10, whiteSpace: 'nowrap', marginLeft: 12 },
  empty: { color: '#9ca3af', fontStyle: 'italic', textAlign: 'center', padding: '40px 0' },
  emptyState: { textAlign: 'center', padding: '48px 24px', backgroundColor: '#fff', borderRadius: 12 },
  emptyTitle: { fontSize: 18, fontWeight: 600, color: '#1a1a2e', margin: '0 0 8px 0' },
  emptyText: { fontSize: 14, color: '#888', margin: '0 0 20px 0' },
  detailCard: { backgroundColor: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', marginBottom: 20 },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  detailTitle: { fontSize: 20, fontWeight: 600, color: '#1a1a2e', margin: '8px 0 0 0' },
  closeBtn: { background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#9ca3af' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', marginBottom: 16, padding: '12px', backgroundColor: '#f8f9fa', borderRadius: 8 },
  infoBlock: { display: 'flex', flexDirection: 'column', gap: 2 },
  infoLabel: { fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' },
  infoVal: { fontSize: 14, color: '#1a1a2e', fontWeight: 500 },
  detailSection: { marginBottom: 14 },
  detailLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 },
  detailText: { fontSize: 14, color: '#374151', margin: 0, lineHeight: 1.6 },
};

export default ApplicationRequestStatus;
